import { IconButton, Stack } from '@form-factory/chatbot-ui';
import { DateFormat, formatUtils } from '@form-factory/shared';
import { Trans, useLingui } from '@lingui/react/macro';
import { useQuery } from '@tanstack/react-query';
import { Form } from 'informed';
import { ChevronsLeft, ChevronsRight } from 'lucide-react';
import { useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { EfErrorAlert } from '@/components/alerts/EfErrorAlert';
import { ClubSelect } from '@/components/eFitness/ClubSelect';
import { ReservationCard } from '@/components/groupClassSchedule/ReservationCard';
import { ScheduleDisplay } from '@/components/groupClassSchedule/ScheduleDisplay';
import { useClassReservations } from '@/hooks/useClassReservations';
import { useRouter } from '@/hooks/useRouter';
import { useUser } from '@/hooks/useUser';
import { aiCoreApi } from '@/services/aiCoreClient';
import { groupClassesKeys } from '@/services/queryClient';
import { useBoundStore } from '@/store/store';

export function GroupClassesRoute() {
  const { t } = useLingui();
  const { popRoute } = useRouter();
  const { user } = useUser();

  const { currentDate, setCurrentDate, currentClubId, setCurrentClubId } =
    useBoundStore(
      useShallow(state => ({
        currentDate: state.groupClasses.currentDate,
        setCurrentDate: state.groupClasses.setCurrentDate,
        currentClubId: state.groupClasses.currentClubId,
        setCurrentClubId: state.groupClasses.setCurrentClubId,
      })),
    );

  useEffect(() => {
    // When there is no clubId selected, use the user's clubId
    if (user?.clubId && !currentClubId) {
      setCurrentClubId(user?.clubId);
    }
  }, [user?.clubId, setCurrentClubId, currentClubId]);

  const { reservations, error: reservationsError } = useClassReservations({
    clubId: currentClubId,
  });

  const {
    data,
    isLoading,
    error: groupClassesError,
  } = useQuery({
    queryKey: [
      ...groupClassesKeys.lists(),
      formatUtils.date(currentDate, DateFormat.DateInput),
      currentClubId,
    ],
    queryFn: async () =>
      aiCoreApi.eFitness.groupClasses.findAll({
        clubId: currentClubId!,
        DateFrom: formatUtils.date(currentDate, DateFormat.DateInput),
        DateTo: formatUtils.date(currentDate, DateFormat.DateInput),
      }),
    enabled: !!currentClubId,
  });

  const handleNextDay = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + 1);

    setCurrentDate(newDate);
  };

  const handlePreviousDay = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() - 1);

    setCurrentDate(newDate);
  };

  const isToday =
    currentDate.getDate() == new Date().getDate() &&
    currentDate.getMonth() == new Date().getMonth() &&
    currentDate.getFullYear() == new Date().getFullYear();

  return (
    <Stack
      title={t`Group Classes`}
      onBack={() => popRoute()}
      onRight={() => setCurrentDate(new Date())}
      rightButtonTitle={isToday ? '' : t`Today`}
    >
      <EfErrorAlert errors={reservationsError} />
      <EfErrorAlert errors={groupClassesError} />

      <Form
        className='mb-4'
        onValueChange={({ values }) => {
          if (values.clubId) {
            setCurrentClubId(values.clubId as string);
          }
        }}
      >
        <ClubSelect
          defaultValue={currentClubId}
          name='clubId'
          label={t`Club`}
        />
      </Form>

      {reservations.length > 0 && (
        <div className='mb-4'>
          <h3 className='mb-2 text-lg font-bold'>
            <Trans>My registrations</Trans>
          </h3>
          {[...reservations]
            .sort(
              (a, b) =>
                new Date(a.startDate).getTime() -
                new Date(b.startDate).getTime(),
            )
            .map(classItem => (
              <ReservationCard
                key={classItem.classReservationId}
                reservationData={classItem}
              />
            ))}
        </div>
      )}
      <div className='mb-4 flex items-center justify-between gap-2'>
        <IconButton size='xs' variant='primary' onClick={handlePreviousDay}>
          <ChevronsLeft />
        </IconButton>
        <h3 className='text-base font-medium'>
          {formatUtils.date(currentDate, DateFormat.WeekDate)}
        </h3>
        <IconButton size='xs' variant='primary' onClick={handleNextDay}>
          <ChevronsRight />
        </IconButton>
      </div>
      <ScheduleDisplay classesData={data ?? []} isLoading={isLoading} />
    </Stack>
  );
}
